import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeDownIcon from '@material-ui/icons/VolumeDown';
import MouseIcon from '@material-ui/icons/Mouse';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CheckIcon from '@material-ui/icons/Check';
import PauseIcon from '@material-ui/icons/Pause';
import StopIcon from '@material-ui/icons/Stop';

import { useTextToSpeech } from 'Hooks';

import styles from './AccessibilityModal.module.scss';

export interface AccessibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AccessibilitySettings {
  fontSize: 'small' | 'normal' | 'large';
  highContrast: boolean;
  darkMode: boolean;
  textToSpeech: boolean;
  speechRate: number;
  speechVolume: number;
  speechPitch: number;
  largePointer: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
  darkMode: false,
  textToSpeech: false,
  speechRate: 1,
  speechVolume: 0.8,
  speechPitch: 1.4,
  largePointer: false,
};

const FONT_SIZE_OPTIONS = [
  { value: 'small' as const, label: 'Küçük' },
  { value: 'normal' as const, label: 'Normal' },
  { value: 'large' as const, label: 'Büyük' },
];

const STORAGE_KEY = 'accessibility-settings';

const applySettings = (newSettings: AccessibilitySettings) => {
  const root = document.documentElement;
  const body = document.body;

  // WCAG 1.4.4 — anlamlı resize. Tek scale html font-size'a uygulanır,
  // MUI rem tabanlı tüm typography (heading, body, button) orantılı büyür.
  const scaleMap: Record<AccessibilitySettings['fontSize'], number> = {
    small: 14 / 16,    // 14px → %87.5
    normal: 1,         // 16px → %100
    large: 20 / 16,    // 20px → %125
  };
  const scale = scaleMap[newSettings.fontSize] ?? 1;

  root.style.setProperty('--accessibility-font-scale', String(scale));
  root.style.setProperty('--accessibility-font-scale-heading', String(scale));
  root.style.setProperty('--accessibility-font-scale-ui', String(scale));

  body.classList.toggle('accessibility-text-scale', scale !== 1);
  body.setAttribute('data-font-size', newSettings.fontSize);

  root.classList.toggle('high-contrast', newSettings.highContrast);
  root.classList.toggle('dark-mode', newSettings.darkMode);
  root.classList.toggle('large-pointer', newSettings.largePointer);

  if (newSettings.textToSpeech) {
    body.setAttribute('data-tts-enabled', 'true');
  } else {
    body.removeAttribute('data-tts-enabled');
  }
};

export const AccessibilityModal: React.FC<AccessibilityModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_SETTINGS);
  const [isWidgetMode, setIsWidgetMode] = useState(false);
  const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { readPage, readSelection, stop, pause, resume, isReading, isPaused, isSupported } =
    useTextToSpeech();

  useEffect(() => {
    const checkScreenSize = () => setIsWidgetMode(window.innerWidth >= 1265);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const merged = { ...DEFAULT_SETTINGS, ...parsed } as AccessibilitySettings;
        setSettings(merged);
        applySettings(merged);
      } catch {
        applySettings(DEFAULT_SETTINGS);
      }
    } else {
      applySettings(DEFAULT_SETTINGS);
    }
  }, []);

  const updateSetting = useCallback(
    <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => {
      setSettings((prev) => {
        const next = { ...prev, [key]: value };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        applySettings(next);

        // Sesli okuma aktifken hız/ses değişirse mevcut okumayı yeni ayarlarla yeniden başlat
        if (isReading && (key === 'speechRate' || key === 'speechVolume' || key === 'speechPitch')) {
          stop();
          if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
          restartTimerRef.current = setTimeout(() => {
            readPage({
              lang: 'tr-TR',
              rate: next.speechRate,
              pitch: next.speechPitch,
              volume: next.speechVolume,
            });
          }, 120);
        }

        // Sesli okuma kapatıldıysa durdur
        if (key === 'textToSpeech' && value === false && isReading) {
          stop();
        }

        return next;
      });
    },
    [isReading, stop, readPage]
  );

  useEffect(() => {
    return () => {
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
    };
  }, []);


  // Click-to-read: TTS aktifken sayfada bir öğeye tıklanınca okur
  /*
useEffect(() => {
  if (!settings.textToSpeech) return undefined;

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    
    // Form input'larını ve widget'ı atla
    const tag = target.tagName.toLowerCase();
    if (['input', 'textarea', 'select'].includes(tag)) return;
    if (target.closest('[data-accessibility-widget="true"]')) return;
    if (target.closest('[role="dialog"], .MuiDialog-root')) return;

    const text =
      target.getAttribute('aria-label') ||
      target.getAttribute('title') ||
      (target.textContent || '').trim();
    if (!text) return;

    const selection = window.getSelection();
    if (selection) {
      stop();
      selection.removeAllRanges();
      const range = document.createRange();
      range.selectNodeContents(target);
      selection.addRange(range);
      readSelection({
        lang: 'tr-TR',
        rate: settings.speechRate,
        pitch: settings.speechPitch,
        volume: settings.speechVolume,
      });
      setTimeout(() => selection.removeAllRanges(), 100);
    }
  };

  document.addEventListener('click', handleClick);
  return () => document.removeEventListener('click', handleClick);
  }, [settings.textToSpeech, settings.speechRate, settings.speechVolume, settings.speechPitch, readSelection, stop]);
*/


// Sayfa değişince ve link/buton tıklanınca durdur
useEffect(() => {
  if (!settings.textToSpeech) return undefined;

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest('[data-accessibility-widget="true"]')) return;

    if (
      target.closest('a[href]') ||
      target.tagName.toLowerCase() === 'button' ||
      target.closest('button')
    ) {
      stop();
    }
  };

  const handleRouteChange = () => stop();

  document.addEventListener('click', handleClick);
  window.addEventListener('popstate', handleRouteChange);

  return () => {
    document.removeEventListener('click', handleClick);
    window.removeEventListener('popstate', handleRouteChange);
  };
}, [settings.textToSpeech, stop]);




  const handleClose = useCallback(() => {
    stop();
    onClose();
  }, [onClose, stop]);

  const handleReset = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_SETTINGS));
    applySettings(DEFAULT_SETTINGS);
    stop();
  }, [stop]);

  const readPageContent = useCallback(() => {
    if (!isSupported) return;
    readPage({
      lang: 'tr-TR',
      rate: settings.speechRate,
      pitch: settings.speechPitch,
      volume: settings.speechVolume,
    });
  }, [isSupported, readPage, settings.speechRate, settings.speechVolume, settings.speechPitch]);

  useEffect(() => {
    if (!settings.textToSpeech) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      const opts = {
        lang: 'tr-TR',
        rate: settings.speechRate,
        pitch: settings.speechPitch,
        volume: settings.speechVolume,
      };

      if (e.altKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        readSelection(opts);
      }
      if (e.altKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        readPageContent();
      }
      if (e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        stop();
      }
      if (
        e.code === 'Space' &&
        isReading &&
        !(e.target as HTMLElement)?.closest('input, textarea, button, [contenteditable]')
      ) {
        e.preventDefault();
        if (isPaused) resume();
        else pause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    settings.textToSpeech,
    settings.speechRate,
    settings.speechVolume,
    isReading,
    isPaused,
    readSelection,
    readPageContent,
    stop,
    pause,
    resume,
  ]);

  if (!isOpen) return null;

  const fontIndex = FONT_SIZE_OPTIONS.findIndex((o) => o.value === settings.fontSize);

  const renderToggle = (
    label: string,
    checked: boolean,
    onToggle: () => void,
    icon?: React.ReactNode
  ) => (
    <button
      type="button"
      className={styles.toggleRow}
      onClick={onToggle}
      aria-pressed={checked}
    >
      <span className={styles.toggleLeft}>
        {icon}
        <span className={styles.toggleText}>{label}</span>
      </span>
      <span
        className={classNames(styles.checkbox, {
          [styles['checkbox--checked']]: checked,
        })}
      >
        <CheckIcon className={styles.checkIcon} />
      </span>
    </button>
  );

  return (
    <>
      {!isWidgetMode && (
        <div
          className={styles.overlay}
          onClick={handleClose}
          role="presentation"
          aria-hidden="true"
        />
      )}

      <div
        className={classNames(styles.container, {
          [styles['container--widget']]: isWidgetMode,
          [styles['container--sheet']]: !isWidgetMode,
        })}
        role="dialog"
        aria-labelledby="accessibility-modal-title"
        aria-modal="true"
        data-accessibility-widget="true"
      >
        <div className={styles.inner}>
          {/* Header */}
          <div className={styles.header}>
            <h2 id="accessibility-modal-title" className={styles.title}>
              <VisibilityIcon />
              Erişilebilirlik
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className={styles.closeBtn}
              title="Kapat"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Font size */}
            <div className={styles.section}>
              <label className={styles.sectionLabel}>
                <TextFieldsIcon />
                Yazı Boyutu
              </label>
              <div className={styles.fontSizeRow}>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={1}
                  value={fontIndex}
                  onChange={(e) => {
                    const idx = parseInt(e.target.value, 10);
                    updateSetting('fontSize', FONT_SIZE_OPTIONS[idx].value);
                  }}
                  className={styles.range}
                  aria-label="Yazı boyutu"
                />
                <span className={styles.fontSizeLabel}>
                  {FONT_SIZE_OPTIONS[fontIndex]?.label}
                </span>
              </div>
            </div>

            {/* Toggles */}
            <div className={styles.toggleGroup}>

              {renderToggle(
                'Büyük İmleç',
                settings.largePointer,
                () => updateSetting('largePointer', !settings.largePointer),
                <MouseIcon fontSize="small" />
              )}
            </div>

            {/* TTS */}
            <div
              className={classNames(styles.ttsSection, {
                [styles['ttsSection--active']]: settings.textToSpeech,
              })}
            >
              <div className={styles.ttsHeader}>
                {renderToggle(
                  'Sesli Okuma',
                  settings.textToSpeech,
                  () => updateSetting('textToSpeech', !settings.textToSpeech),
                  <VolumeUpIcon fontSize="small" />
                )}
              </div>

              {settings.textToSpeech && (
                <div className={styles.ttsControls}>
                  <div className={styles.ttsButtonRow}>
                    <button
                      type="button"
                      onClick={readPageContent}
                      disabled={isReading || !isSupported}
                      className={styles.ttsButton}
                    >
                      <PlayArrowIcon fontSize="small" />
                      Sayfayı Oku
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        readSelection({
                          lang: 'tr-TR',
                          rate: settings.speechRate,
                          pitch: settings.speechPitch,
                          volume: settings.speechVolume,
                        })
                      }
                      disabled={isReading || !isSupported}
                      className={classNames(
                        styles.ttsButton,
                        styles['ttsButton--secondary']
                      )}
                    >
                      <VolumeDownIcon fontSize="small" />
                      Seçili Oku
                    </button>
                  </div>

                  <div className={styles.ttsButtonRow}>
                    <button
                      type="button"
                      onClick={() => (isPaused ? resume() : pause())}
                      disabled={!isReading || !isSupported}
                      className={classNames(
                        styles.ttsButton,
                        styles['ttsButton--secondary']
                      )}
                
                    >
                      <PauseIcon fontSize="small" />
                      {isPaused ? 'Sürdür' : 'Duraklat'}
                    </button>
                    <button
                      type="button"
                      onClick={stop}
                      disabled={!isReading || !isSupported}
                      className={classNames(
                        styles.ttsButton,
                        styles['ttsButton--secondary']
                      )}
                 
                    >
                      <StopIcon fontSize="small" />
                      Durdur
                    </button>
                  </div>

                  <div className={styles.rangeRow}>
                    <label className={styles.rangeLabel}>
                      Okuma Hızı: {settings.speechRate.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min={0.5}
                      max={2}
                      step={0.1}
                      value={settings.speechRate}
                      onChange={(e) =>
                        updateSetting('speechRate', parseFloat(e.target.value))
                      }
                      className={styles.range}
                      aria-label="Okuma hızı"
                    />
                  </div>

                  <div className={styles.rangeRow}>
                    <label className={styles.rangeLabel}>
                      Ses Seviyesi: {Math.round(settings.speechVolume * 100)}%
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={settings.speechVolume}
                      onChange={(e) =>
                        updateSetting('speechVolume', parseFloat(e.target.value))
                      }
                      className={styles.range}
                      aria-label="Ses seviyesi"
                    />
                  </div>
                </div>
              )}
            </div>

            
            {/* Dark mode */}

            {/*
            {renderToggle(
              'Koyu Tema',
              settings.darkMode,
              () => updateSetting('darkMode', !settings.darkMode),
              settings.darkMode ? (
                <Brightness3Icon fontSize="small" />
              ) : (
                <Brightness7Icon fontSize="small" />
              )
            )}
            */}

            {/* Active info */}
            {(settings.highContrast ||
              settings.darkMode ||
              settings.fontSize !== 'normal') && (
              <div className={styles.infoBox}>
                <p className={styles.infoTitle}>✓ Erişilebilirlik ayarları aktif</p>
                <p className={styles.infoSubtitle}>
                  Değişiklikler otomatik olarak kaydedilir
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              type="button"
              onClick={handleClose}
              className={classNames(styles.footerBtn, styles['footerBtn--primary'])}
            >
              Kapat
            </button>
            <button
              type="button"
              onClick={handleReset}
              className={classNames(styles.footerBtn, styles['footerBtn--secondary'])}
            >
              <RefreshIcon fontSize="small" />
              Sıfırla
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccessibilityModal;
